import type { NodeEntry } from 'slate';

import type { RenderTextOptions } from './text';
import { renderText } from './text';
import { Queries } from '../../slate-api';
import type { RenderModelCannedField } from '../../slate-api';
import {
  activeInputFieldBackgroundColor,
  defaultInputFieldBackgroundColor,
  inputFieldBorderColor,
} from '../colors';
import type { RenderContext } from '../engine/render-context';

/**
 * Canned fields render exactly like a plain input field on the canvas —
 * highlighted background, dashed underline, and the current value drawn as
 * regular text. The Slate node's child `text` is kept in sync with the
 * `CannedField.value` DB row by a client hook (see
 * `useSyncCannedFieldValuesToSlate`), so the render model measures the real
 * value (not a placeholder) and there is no React overlay layered on top.
 */
export function renderCannedField(
  renderContext: RenderContext,
  rMCannedFieldEntry: NodeEntry<RenderModelCannedField>,
  options: RenderTextOptions
) {
  const { editor } = renderContext;
  const isPDFExport = renderContext.withContext('isPDFExport');
  const [, rMCannedFieldPath] = rMCannedFieldEntry;
  const { baselinePx, lineMaxDescentPx } = options;
  const isSelected = renderContext.isSelectionInsideCMNode();

  renderContext.renderChildren(rMCannedFieldEntry, { text: renderText }, {
    ...options,
    customHighlight: isPDFExport
      ? undefined
      : {
          color: isSelected
            ? activeInputFieldBackgroundColor.toString()
            : defaultInputFieldBackgroundColor.toString(),
        },
    inlineVoid: {
      caret: 'hidden',
      showText: true,
    },
    overrideCursor: {
      cursor: 'pointer',
      key: `${rMCannedFieldPath}-cursor`,
    },
  } satisfies RenderTextOptions);

  if (!isPDFExport)
    renderContext.drawHorizontalLine({
      color: inputFieldBorderColor.toString(),
      dashPattern: isSelected ? [6, 4] : [3, 2],
      endX: Queries.getRMNodeWidthPx(editor, rMCannedFieldEntry),
      startX: 0,
      thickness: isSelected ? 2 : 1,
      y: baselinePx + lineMaxDescentPx,
    });
}
